// src/stores/register.ts
import { defineStore } from 'pinia';
import AuthSlice, { type InfoRegistrationPayload } from '@/api/AuthSlice';
import { clearPendingReferralCode, getPendingReferralCode } from '@/utils/referrals';

type GenderUI = 'female' | 'male' | 'm_f' | 'm_m' | 'f_f';
type OrientationKey = 'hetero' | 'gay' | 'lesbian' | 'bi' | 'ace' | 'unsure';
type ShowForKey = 'women' | 'men' | 'm_f' | 'm_m' | 'f_f' | 'all';
type FamilyStatusUI = 'single' | 'married' | 'widow' | 'divorced';

const REGISTRATION_FIELD_LABELS: Record<string, string> = {
    name: "имя",
    birth_date: "дата рождения",
    email: "email",
    phone: "номер телефона",
    gender: "пол",
    sexual_orientation: "сексуальная ориентация",
    family_status: "семейное положение",
    relationship_preference_id: "цель знакомства",
    interests: "минимум 3 интереса",
    show_me: "кого показывать",
    photos: "минимум 2 фотографии",
};

function getBackendValidationFields(error: any): string[] {
    const data = error?.response?.data?.data ?? error?.response?.data;
    const errors = data?.errors ?? data?.validation_errors;
    if (!errors || typeof errors !== "object") return [];

    return Object.keys(errors).map((field) => REGISTRATION_FIELD_LABELS[field] || field);
}

function isReferralValidationError(error: unknown) {
    return (error as { response?: { status?: number } }).response?.status === 422;
}

export const useRegisterStore = defineStore('register', {
    state: () => ({
        step: 1,
        name: '' as string,
        dobISO: '' as string,      // YYYY-MM-DD
        gender: null as GenderUI | null,
        showGender: false,
        orientation: null as OrientationKey | null,
        showOrientation: false,
        showFor: [] as ShowForKey[],
        family_status: null as FamilyStatusUI | null,
        showFamilyStatus: false,
        relationship_preference_id: null as number | null,
        interests: [] as number[],
        phone: '' as string,
        email: '' as string,
        birth_time: '' as string,
        loading: false as boolean,
        error: '' as string,
        referralWarning: '' as string,
    }),
    getters: {
        progress: (s) => Math.min(s.step / 10, 1),
    },
    actions: {
        setStep(v: number) {
            this.step = v;
        },
        nextStep() {
            this.step += 1;
        },
        setName(v: string) {
            this.name = v;
        },
        setGender(v: GenderUI | null) {
            this.gender = v;
        },
        setShowGender(v: boolean) {
            this.showGender = v;
        },
        setOrientation(v: OrientationKey | null) {
            this.orientation = v;
        },
        setShowOrientation(v: boolean) {
            this.showOrientation = v;
        },
        setShowFor(v: ShowForKey[]) {
            this.showFor = v;
        },
        setInterests(v: number[]) {
            this.interests = v;
        },
        setDobISO(v: string) {
            this.dobISO = v;
        },
        setFamilyStatus(v: FamilyStatusUI | null) {
            this.family_status = v;
        },
        setShowFamilyStatus(v: boolean) {
            this.showFamilyStatus = v;
        },
        setRelationshipPrefId(v: number | null) {
            this.relationship_preference_id = v;
        },
        setEmail(v: string) {
            this.email = v;
        },
        setPhone(v: string) {
            this.phone = v;
        },
        setBirthTime(v: string) {
            this.birth_time = v;
        },

        _mapShowFor(): string[] {
            if (!this.showFor || this.showFor.length === 0) {
                return ["female"];
            }

            const map: Record<ShowForKey, string[]> = {
                women: ["female"],
                men: ["male"],
                m_f: ["m_f"],
                m_m: ["m_m"],
                f_f: ["f_f"],
                all: ["female", "male", "m_f", "m_m", "f_f"]
            };

            const result = new Set<string>();

            for (const key of this.showFor) {
                for (const v of map[key]) {
                    result.add(v);
                }
            }

            return Array.from(result);
        },

        _mapGender(): 'male' | 'female' | 'm_f' | 'm_m' | 'f_f' | undefined {
            switch (this.gender) {
                case 'male':
                    return 'male';
                case 'female':
                    return 'female';
                case 'm_f':
                    return 'm_f';
                case 'm_m':
                    return 'm_m';
                case 'f_f':
                    return 'f_f';
                default:
                    return undefined;
            }
        },

        _mapOrientation(): 'hetero' | 'gay' | 'lesbian' | 'bisexual' | 'asexual' | 'not_decided' {
            switch (this.orientation) {
                case 'hetero':
                    return 'hetero';
                case 'gay':
                    return 'gay';
                case 'lesbian':
                    return 'lesbian';
                case 'bi':
                    return 'bisexual';
                case 'ace':
                    return 'asexual';
                case 'unsure':
                    return 'not_decided';
                default:
                    return 'not_decided';
            }
        },

        _mapFamilyStatus(): 'not_married' | 'married' | 'widow_er' | 'divorced' | undefined {
            switch (this.family_status) {
                case 'single':
                    return 'not_married';
                case 'married':
                    return 'married';
                case 'widow':
                    return 'widow_er';
                case 'divorced':
                    return 'divorced';
                default:
                    return undefined;
            }
        },

        async submitRegistration(photoUrls?: string[]) {
            this.loading = true;
            this.error = "";
            this.referralWarning = "";

            try {
                const registrationPhotos: unknown[] = photoUrls ?? JSON.parse(localStorage.getItem("register_photos") || "[]");
                const validatedPhotos = registrationPhotos
                    .filter((url: unknown): url is string => typeof url === "string" && url.length > 0);
                const missingFields: string[] = [];

                if (!this.name.trim()) missingFields.push(REGISTRATION_FIELD_LABELS.name);
                if (!this.dobISO.trim()) missingFields.push(REGISTRATION_FIELD_LABELS.birth_date);
                const isVkRegistration = localStorage.getItem("vk_registration") === "1";
                if (!isVkRegistration && !this.email.trim()) missingFields.push(REGISTRATION_FIELD_LABELS.email);
                if (!isVkRegistration && !this.phone.trim()) missingFields.push(REGISTRATION_FIELD_LABELS.phone);
                if (!this.gender) missingFields.push(REGISTRATION_FIELD_LABELS.gender);
                if (!this.orientation) missingFields.push(REGISTRATION_FIELD_LABELS.sexual_orientation);
                if (!this.family_status) missingFields.push(REGISTRATION_FIELD_LABELS.family_status);
                if (!this.relationship_preference_id) missingFields.push(REGISTRATION_FIELD_LABELS.relationship_preference_id);
                if (!Array.isArray(this.interests) || this.interests.length < 3) missingFields.push(REGISTRATION_FIELD_LABELS.interests);
                if (!Array.isArray(this.showFor) || this.showFor.length === 0) missingFields.push(REGISTRATION_FIELD_LABELS.show_me);
                if (validatedPhotos.length < 2) missingFields.push(REGISTRATION_FIELD_LABELS.photos);

                if (missingFields.length > 0) {
                    throw new Error(`Не удалось завершить регистрацию. Заполните: ${missingFields.join(", ")}.`);
                }

                const referralCode = getPendingReferralCode();
                const payload: InfoRegistrationPayload = {
                    name: this.name,
                    birth_date: this.dobISO,
                    ...(!isVkRegistration || this.email ? { email: this.email || null } : {}),
                    ...(!isVkRegistration || this.phone ? { phone: this.phone } : {}),
                    gender: this._mapGender(),
                    sexual_orientation: this._mapOrientation(),
                    family_status: this._mapFamilyStatus(),
                    relationship_preference_id: this.relationship_preference_id,
                    interests: this.interests,
                    show_family_status: this.showFamilyStatus,
                    show_me: this._mapShowFor(),
                    show_my_gender: this.showGender,
                    show_my_orientation: this.showOrientation,
                    ...(this.birth_time ? { birth_time: this.birth_time } : {}),
                    ...(referralCode ? { referral_code: referralCode } : {}),
                };

                console.log("📤 Payload JSON:", JSON.stringify(payload, null, 2));

                let infoRegistrationResponse;
                try {
                    infoRegistrationResponse = await AuthSlice.PostInfoRegistration(payload);
                } catch (error: unknown) {
                    if (!referralCode || !isReferralValidationError(error)) throw error;

                    const { referral_code: _invalidReferralCode, ...payloadWithoutReferral } = payload;
                    infoRegistrationResponse = await AuthSlice.PostInfoRegistration(payloadWithoutReferral);
                    this.referralWarning = "Код приглашения не удалось применить. Регистрация продолжена без него.";
                }

                const { data } = infoRegistrationResponse;
                clearPendingReferralCode();

                if (data?.token) {
                    localStorage.setItem("access_token", data.token);
                    console.log("✅ Access token обновлён:", data.token);
                } else {
                    console.warn("⚠️ Access token не получен, возможны проблемы с авторизацией");
                }

                const marketResponse = await AuthSlice.SetMarket();
                if (marketResponse?.meta?.error) {
                    throw new Error(marketResponse.meta.error.message || "Не удалось сохранить market");
                }

                // Photos are required: registration is not complete until they are uploaded.
                const storedPhotos: unknown[] = photoUrls ?? JSON.parse(localStorage.getItem("register_photos") || "[]");
                const savedPhotos = storedPhotos
                    .filter((url: unknown): url is string => typeof url === "string" && url.length > 0);
                if (savedPhotos.length < 2) {
                    throw new Error("Добавьте как минимум 2 фотографии");
                }

                const files = await Promise.all(
                    savedPhotos.map(async (url: string, i: number) => {
                        const response = await fetch(url);
                        if (!response.ok) throw new Error("Не удалось подготовить фотографии к загрузке");

                        const blob = await response.blob();
                        return new File([blob], `photo_${i + 1}.jpg`, { type: blob.type || "image/jpeg" });
                    })
                );

                const uploadRes = await AuthSlice.UploadPhotos(files);
                if (
                    uploadRes?.meta?.error ||
                    uploadRes?.meta?.status === 0 ||
                    (uploadRes?.meta?.status && uploadRes.meta.status >= 400)
                ) {
                    throw new Error(uploadRes.meta.error?.message || "Не удалось загрузить фотографии");
                }

                console.log("✅ Фото успешно загружены:", uploadRes);

                console.log("🎉 Регистрация завершена успешно");
                return true;

            } catch (e: any) {
                console.error("❌ submitRegistration error:", e);
                if (e?.response?.status === 406) {
                    const backendFields = getBackendValidationFields(e);
                    this.error = backendFields.length > 0
                        ? `Сервер не принял регистрацию. Проверьте: ${backendFields.join(", ")}.`
                        : "Сервер не принял регистрацию: одно или несколько обязательных полей не заполнены. Начните регистрацию заново.";
                    return false;
                }
                const isPhotoUploadError = e?.config?.url?.includes?.("/users/photos");
                this.error = isPhotoUploadError
                    ? "Не удалось загрузить фотографии. Попробуйте ещё раз."
                    : (e.message || "Ошибка при регистрации");
                return false;
            } finally {
                this.loading = false;
            }
        }
    },
});
